import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { LoginRequest } from '../../../core/auth/auth.models';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe
  ],
  template: `
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="login-form"
    >
      <div class="login-form__field">
        <label for="email" class="login-form__label">{{ 'auth.email' | t }}</label>
        <input
          id="email"
          type="email"
          formControlName="email"
          class="login-form__input"
          [placeholder]="'auth.enterEmail' | t"
        />
      </div>

      @if (tenantSlug) {
        <div class="login-form__hint">
          {{ 'Tenant context' | t }}: <strong>{{ tenantSlug }}</strong>
        </div>
      }

      <div class="login-form__field">
        <label for="password" class="login-form__label">{{ 'auth.password' | t }}</label>
        <input
          id="password"
          type="password"
          formControlName="password"
          class="login-form__input"
          [placeholder]="'auth.enterPassword' | t"
        />
      </div>

      @if (twoFactorRequired) {
        <div class="login-form__field">
          <label for="totpCode" class="login-form__label">{{ 'auth.2faCode' | t }}</label>
          <input
            id="totpCode"
            type="text"
            formControlName="totpCode"
            class="login-form__input"
            maxlength="32"
            [placeholder]="'auth.2faCodePlaceholder' | t"
          />
        </div>

        <label class="login-form__checkbox">
          <input type="checkbox" formControlName="trustDevice" />
          <span>{{ 'auth.trustDevice' | t }}</span>
        </label>
      }

      @if (errorText) {
        <div class="login-form__error">
          {{ errorText | t }}
        </div>
      }

      <button
        type="submit"
        class="login-form__submit"
        [disabled]="isSubmitting || !tenantSlug"
      >
        {{ (isSubmitting ? 'auth.signingIn' : 'auth.signIn') | t }}
      </button>
    </form>
  `,
  styles: [`
    .login-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .login-form__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .login-form__label {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }

    .login-form__input {
      height: 42px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      outline: none;
    }

    .login-form__input:focus {
      border-color: var(--tenant-primary, #2563eb);
    }

    .login-form__error {
      color: #b91c1c;
      font-size: 14px;
    }

    .login-form__hint {
      color: #6b7280;
      font-size: 13px;
    }

    .login-form__checkbox {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #374151;
      font-size: 13px;
    }

    .login-form__submit {
      height: 42px;
      border: none;
      border-radius: 8px;
      background: var(--tenant-primary, #2563eb);
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .login-form__submit:disabled {
      opacity: 0.7;
      cursor: default;
    }
  `]
})
export class LoginFormComponent
{
  private readonly formBuilder = inject(FormBuilder);

  @Input()
  public isSubmitting = false;

  @Input()
  public errorText: string | null = null;

  @Input()
  public tenantSlug: string | null = null;

  @Input()
  public twoFactorRequired = false;

  @Output()
  public readonly submitted = new EventEmitter<LoginRequest>();

  protected readonly form = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
    totpCode: [''],
    trustDevice: this.formBuilder.nonNullable.control(false),
  });

  protected onSubmit(): void
  {
    const rawValue = this.form.getRawValue();

    if (!this.tenantSlug || this.form.invalid)
    {
      this.form.markAllAsTouched();
      return;
    }

    this.submitted.emit({
      companySlug: this.normalizeSlug(this.tenantSlug),
      email: rawValue.email ?? '',
      password: rawValue.password ?? '',
      totpCode: this.twoFactorRequired ? (rawValue.totpCode?.trim() || undefined) : undefined,
      trustDevice: this.twoFactorRequired ? !!rawValue.trustDevice : undefined,
    });
  }

  private normalizeSlug(value: string): string {
    return value.trim().toLowerCase();
  }
}
